<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { useMachine } from "@xstate/vue"
import { createFormAuthFirstLoginMachine } from "./form-auth-first-login.machine"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: ({ named }: { named: (key: string) => boolean }) =>
                named("lastStep")
                    ? "Dernière étape à franchir..."
                    : "Première connexion",
            noAccount: "Vous n'avez pas de compte ?",
            subscribe: "S'inscrire",
            alreadyAccount: "Vous avez déjà un compte ?",
            login: "Connectez-vous",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()

const machineInput = { getRoute: () => route }
const machine = createFormAuthFirstLoginMachine(machineInput)
const { snapshot, send } = useMachine(machine, { input: machineInput })

// ================================
// COMPUTED
// ================================
const getFooterLinks = computed(() => [
    { text: t("noAccount"), label: t("subscribe"), link_to: "/inscription" },
    { text: t("alreadyAccount"), label: t("login"), link_to: "/login" },
])
/** État courant de la machine (nom de l'étape). */
const currentState = computed(() => snapshot.value?.value ?? "idle")

/** Contexte courant (user, validation, idcompany, hascompany). */
const context = computed(() => snapshot.value?.context ?? null)

/** Handler next pour l'étape email. */
function onNextEmail(payload?: { _id?: string; username?: string }): void {
    send({ type: "NEXT", value: payload ?? undefined })
}

/** Handler next pour l'étape confirmation. */
function onNextConfirmation(payload: {
    validation: string
    idcompany?: string | null
    hascompany?: boolean
}): void {
    send({
        type: "NEXT",
        validation: payload.validation,
        idcompany: payload.idcompany ?? undefined,
        hascompany: payload.hascompany ?? undefined,
    })
}

// ================================
// LIFECYCLE
// ================================
onMounted(() => {
    send({ type: "REDIRECTION" })
})
</script>

<template>
    <BaseCard>
        <template #title>
            <FormAuthHeader
                :title="
                    t('title', {
                        lastStep: currentState === 'FormAuthFirstLoginInfo',
                    })
                "
            />
        </template>
        <template #content>
            <div class="flex flex-col gap-6 py-6">
                <FormAuthFirstLoginEmail
                    v-if="currentState === 'FormAuthFirstLoginEmail'"
                    key="email"
                    @next="onNextEmail"
                />
                <FormAuthFirstLoginConfirmation
                    v-else-if="
                        currentState === 'FormAuthFirstLoginConfirmation'
                    "
                    key="confirmation"
                    :user="context?.user ?? null"
                    @next="onNextConfirmation"
                />
                <FormAuthFirstLoginInfo
                    v-else-if="currentState === 'FormAuthFirstLoginInfo'"
                    key="info"
                    :user="context?.user ?? null"
                    :validation="context?.validation ?? null"
                    :idcompany="context?.idcompany ?? null"
                    :hascompany="context?.hascompany ?? false"
                />
            </div>
            <FooterLogin :links="getFooterLinks" :forget-password="true" />
        </template>
    </BaseCard>
</template>
